import { Editor } from '@tiptap/react'
import { useCallback, useReducer } from 'react'

import { parseHTMLToPlainText } from '@/utils/parseHTMLToPlainText'

export type State = {
  isModalOpen: boolean
  url: string | null
  linkText: string | null
  isEditingLink: boolean
}

const initialState: State = {
  isModalOpen: false,
  url: null,
  linkText: null,
  isEditingLink: false,
}

export const useLinkEditor = ({ editor }: { editor: Editor }) => {
  const [{ isEditingLink, isModalOpen, linkText, url }, setState] = useReducer(
    (state: State, payload: Partial<State>): State => ({
      ...state,
      ...payload,
    }),
    initialState
  )

  const openEditLinkModal = useCallback(() => {
    const anchorTagLength = 4
    const previousUrl = editor.getAttributes('link').href as string
    const html = editor.getHTML()

    const selectionPosition = editor.state.selection.to
    const previousUrlIndex = html.indexOf(previousUrl, selectionPosition)

    const start = html.lastIndexOf('<a', previousUrlIndex)

    const end = html.indexOf('</a>', previousUrlIndex) + anchorTagLength

    const htmlText = html.substring(start, end)

    const text = parseHTMLToPlainText(htmlText)

    setState({
      url: previousUrl,
      linkText: text,
      isModalOpen: true,
      isEditingLink: true,
    })
  }, [editor])

  const openAddLinkModal = useCallback(() => {
    const { from, to } = editor.state.selection
    const hasSelection = from !== to

    const linkText = hasSelection
      ? editor.state.doc.textBetween(from, to)
      : null

    setState({
      url: null,
      linkText,
      isModalOpen: true,
    })
  }, [editor])

  const addLink = useCallback(() => {
    if (!url) return

    const { from, to } = editor.state.selection
    const hasSelection = from !== to

    if (hasSelection) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url, target: '_blank' })
        .run()
    } else {
      const formattedUrl = formatUrl(url)

      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: formattedUrl, target: '_blank' })
        .insertContent(linkText)
        .insertContent(' ')
        .run()
    }

    setState({
      url: null,
      linkText,
      isEditingLink: false,
      isModalOpen: false,
    })
  }, [url, linkText, editor])

  const editLink = useCallback(() => {
    if (!url || !linkText) return

    editor.chain().extendMarkRange('link').insertContent(' ').focus().run()

    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: formatUrl(url), target: '_blank' })
      .insertContent(linkText)
      .run()

    setState({
      url: null,
      isModalOpen: false,
      isEditingLink: false,
    })
  }, [editor, url, linkText])

  const handleCloseModal = () => {
    setState({
      url: null,
      isModalOpen: false,
      isEditingLink: false,
      linkText: null,
    })
  }

  const onSubmit = () => {
    if (isEditingLink) {
      return editLink()
    }
    return addLink()
  }

  return {
    isModalOpen,
    linkText,
    url,
    handleCloseModal,
    openAddLinkModal,
    openEditLinkModal,
    setLinkText: (text: string) => setState({ linkText: text }),
    setUrl: (url: string) => setState({ url }),
    onSubmit,
  }
}
const formatUrl = (url: string) => {
  return url.startsWith('http') ? url : `http://${url}`
}
